/**
 * BasicInfoStep — Step 1 of the ProfileComplete v2.0 wizard.
 *
 * Density redesign (Stripe / Razorpay-style):
 *   - p-inputtext-sm everywhere (≈32px tall, was ≈40px)
 *   - text-xs labels with mb-1 gap (was text-sm + mb-2)
 *   - mb-2 between fields, mb-4 between sections
 *   - Section header is a single uppercase caption, not an icon+title+subtitle stack
 *   - Pincode result renders as inline text under the input, not a banner
 *   - Smart per-field widths (pincode narrow, name medium, email wide, address full)
 *
 * Skill compliance: P5 inline validation, P9 inputMode/autoCapitalize, P13 600ms
 * pincode debounce, P16 client trim/regex/maxLength, Hard rule 6 (no hex).
 */
import { useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../config/axios.config';
import {
    validateFirstName,
    validateLastName,
    validateMobile,
    validateEmail,
    validatePincode,
} from './validators';

const PINCODE_DEBOUNCE_MS = 600;

const BasicInfoStep = ({
    value,
    errors,
    onChange,
    onErrors,
    pincodeMessage,
    onPincodeMessage,
    showLocationFields,
    onShowLocationFields,
    pincodeLoading,
    onPincodeLoading,
    saving = false,
}) => {
    const debounceRef = useRef(null);

    // ── Pincode → city/state lookup (debounced) ───────────────────────────
    useEffect(() => {
        const pin = (value.pincode || '').trim();

        if (pin.length < 6) {
            onPincodeMessage({ show: false, type: '', text: '' });
        }

        if (!/^\d{6}$/.test(pin)) {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            onPincodeLoading(true);
            try {
                const response = await axiosInstance.get(`/location/pincode/${pin}`);
                if (response.data?.success && response.data?.data) {
                    const { city, state, area } = response.data.data;
                    onChange({
                        city: city || '',
                        state: state || '',
                        ...(area ? { address: value.address || area } : {}),
                    });
                    onShowLocationFields(true);
                    onPincodeMessage({
                        show: true,
                        type: 'success',
                        text: `${city || ''}${state ? `, ${state}` : ''}`,
                    });
                    onErrors({ city: '', state: '', pincode: '' });
                } else {
                    onShowLocationFields(true);
                    onPincodeMessage({
                        show: true,
                        type: 'warn',
                        text: 'Pincode not found — fill city and state manually.',
                    });
                }
            } catch {
                onShowLocationFields(true);
                onPincodeMessage({
                    show: true,
                    type: 'warn',
                    text: 'Pincode not found — fill city and state manually.',
                });
            } finally {
                onPincodeLoading(false);
            }
        }, PINCODE_DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.pincode]);

    // ── Field handlers ────────────────────────────────────────────────────
    const handleField = (field, val, validator) => {
        onChange({ [field]: val });
        if (errors[field]) {
            const next = validator ? validator(val) : '';
            onErrors({ [field]: next });
        }
    };

    const handleBlur = (field, validator) => {
        const err = validator(value[field]);
        onErrors({ [field]: err });
    };

    return (
        <div>
            {/* ─ Section A — Personal ─────────────────────────────── */}
            <SectionPanel icon="pi-user" title="Personal details" subtitle="Tell us who you are">
            <div className="grid">
                {/* First name */}
                <Field label="First name" required error={errors.first_name} cols="col-6 md:col-3">
                    <InputText
                        value={value.first_name}
                        onChange={(e) => handleField('first_name', e.target.value, validateFirstName)}
                        onBlur={() => handleBlur('first_name', validateFirstName)}
                        className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.first_name })}
                        placeholder="Rahul"
                        maxLength={50}
                        autoCapitalize="words"
                        autoComplete="given-name"
                        aria-invalid={!!errors.first_name}
                        disabled={saving}
                    />
                </Field>

                {/* Last name */}
                <Field label="Last name" required error={errors.last_name} cols="col-6 md:col-3">
                    <InputText
                        value={value.last_name}
                        onChange={(e) => handleField('last_name', e.target.value, validateLastName)}
                        onBlur={() => handleBlur('last_name', validateLastName)}
                        className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.last_name })}
                        placeholder="Sharma"
                        maxLength={50}
                        autoCapitalize="words"
                        autoComplete="family-name"
                        aria-invalid={!!errors.last_name}
                        disabled={saving}
                    />
                </Field>

                {/* Mobile */}
                <Field label="Mobile" required error={errors.mobile_number} cols="col-6 md:col-3">
                    <div className="p-inputgroup p-inputgroup-sm">
                        <span className="p-inputgroup-addon">+91</span>
                        <InputText
                            value={value.mobile_number}
                            onChange={(e) => {
                                const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 10);
                                handleField('mobile_number', digits, validateMobile);
                            }}
                            onBlur={() => handleBlur('mobile_number', validateMobile)}
                            className={classNames('p-inputtext-sm', { 'p-invalid': !!errors.mobile_number })}
                            placeholder="98765 43210"
                            maxLength={10}
                            inputMode="numeric"
                            autoComplete="tel-national"
                            aria-invalid={!!errors.mobile_number}
                            disabled={saving}
                        />
                    </div>
                </Field>

                {/* Email — same width as the other 3 so all 4 fit in a single
                    desktop row (col-3 each), 2 per row on mobile (col-6 each). */}
                <Field label="Email" required error={errors.email} cols="col-6 md:col-3">
                    <InputText
                        value={value.email}
                        onChange={(e) => handleField('email', e.target.value, validateEmail)}
                        onBlur={() => handleBlur('email', validateEmail)}
                        className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.email })}
                        placeholder="you@example.com"
                        maxLength={100}
                        inputMode="email"
                        autoCapitalize="none"
                        autoComplete="email"
                        aria-invalid={!!errors.email}
                        disabled={saving}
                    />
                </Field>
            </div>
            </SectionPanel>

            {/* ─ Section B — Address ──────────────────────────────── */}
            <SectionPanel icon="pi-map-marker" title="Address" subtitle="Where you're based">
            <div className="grid">
                {/* Pincode (narrow — 6 digits) */}
                <Field
                    label="Pincode"
                    error={errors.pincode}
                    cols="col-6 md:col-4"
                    inlineHint={pincodeMessage?.show && !errors.pincode
                        ? { text: pincodeMessage.text, type: pincodeMessage.type }
                        : null}
                >
                    <div className="p-inputgroup p-inputgroup-sm">
                        <InputText
                            value={value.pincode}
                            onChange={(e) => {
                                const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
                                handleField('pincode', digits, (v) => validatePincode(v));
                            }}
                            onBlur={() => handleBlur('pincode', (v) => validatePincode(v))}
                            className={classNames('p-inputtext-sm', { 'p-invalid': !!errors.pincode })}
                            placeholder="560001"
                            maxLength={6}
                            inputMode="numeric"
                            autoComplete="postal-code"
                            aria-invalid={!!errors.pincode}
                            disabled={saving}
                        />
                        {pincodeLoading && (
                            <span className="p-inputgroup-addon" aria-label="Looking up pincode">
                                <ProgressSpinner
                                    style={{ width: '1rem', height: '1rem' }}
                                    strokeWidth="4"
                                />
                            </span>
                        )}
                    </div>
                </Field>

                {/* City */}
                {showLocationFields && (
                    <Field label="City" required={!!value.pincode} error={errors.city} cols="col-6 md:col-4">
                        <InputText
                            value={value.city}
                            onChange={(e) => handleField('city', e.target.value)}
                            onBlur={() => {
                                if (value.pincode && !value.city?.trim()) {
                                    onErrors({ city: 'City is required.' });
                                }
                            }}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.city })}
                            placeholder="Bengaluru"
                            maxLength={80}
                            autoCapitalize="words"
                            autoComplete="address-level2"
                            aria-invalid={!!errors.city}
                            disabled={saving}
                        />
                    </Field>
                )}

                {/* State */}
                {showLocationFields && (
                    <Field label="State" required={!!value.pincode} error={errors.state} cols="col-6 md:col-4">
                        <InputText
                            value={value.state}
                            onChange={(e) => handleField('state', e.target.value)}
                            onBlur={() => {
                                if (value.pincode && !value.state?.trim()) {
                                    onErrors({ state: 'State is required.' });
                                }
                            }}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.state })}
                            placeholder="Karnataka"
                            maxLength={80}
                            autoCapitalize="words"
                            autoComplete="address-level1"
                            aria-invalid={!!errors.state}
                            disabled={saving}
                        />
                    </Field>
                )}

                {/* Address — full row */}
                <Field label="Street address" cols="col-12">
                    <InputTextarea
                        value={value.address}
                        onChange={(e) => handleField('address', e.target.value)}
                        className="w-full p-inputtext-sm"
                        rows={2}
                        autoResize
                        placeholder="House / building, street, locality"
                        maxLength={500}
                        autoCapitalize="sentences"
                        disabled={saving}
                    />
                </Field>
            </div>
            </SectionPanel>
        </div>
    );
};

/* ── Section panel — PrimeReact Panel with a custom header.
     - Distinct header bar (theme's panel-header tint) separates each
       section visually, like a card with header + body.
     - Icon chip on the left, title + subtitle on the right.
     - toggleable={false} so the form fields stay visible (no collapse). */
const SectionPanel = ({ icon, title, subtitle, children, className = '' }) => {
    const headerContent = (
        <div className="flex align-items-center gap-2">
            {icon && (
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width: '1.75rem',
                        height: '1.75rem',
                        backgroundColor: 'var(--primary-50)',
                        color: 'var(--primary-color)',
                        fontSize: '0.85rem',
                    }}
                >
                    <i className={`pi ${icon}`} aria-hidden="true" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-900 font-semibold text-sm">{title}</div>
                {subtitle && (
                    <div className="text-500 font-normal" style={{ fontSize: '0.7rem', marginTop: '0.1rem' }}>
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Panel
            header={headerContent}
            toggleable={false}
            className={classNames('mb-3', className)}
            pt={{
                header: { className: 'py-2 px-3' },
                content: { className: 'p-3' },
            }}
        >
            {children}
        </Panel>
    );
};

/* ── Field wrapper — tighter rhythm: text-xs label, mb-1 to input, mb-2 between fields ── */
const Field = ({ label, required, error, hint, inlineHint, cols = 'col-12', children }) => (
    <div className={classNames('mb-2', cols)}>
        <label className="block text-700 text-xs font-medium mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children}
        {inlineHint && (
            <small
                className={classNames(
                    'block mt-1 text-xs',
                    inlineHint.type === 'success' ? 'text-green-600' : 'text-orange-600'
                )}
            >
                <i
                    className={classNames(
                        'pi mr-1',
                        inlineHint.type === 'success' ? 'pi-check-circle' : 'pi-info-circle'
                    )}
                    style={{ fontSize: '0.65rem' }}
                    aria-hidden="true"
                />
                {inlineHint.text}
            </small>
        )}
        {hint && !error && !inlineHint && (
            <small className="text-500 block mt-1 text-xs">{hint}</small>
        )}
        {error && (
            <small role="alert" className="p-error block mt-1 text-xs">{error}</small>
        )}
    </div>
);

/* ── runValidations: returns the errors object the parent shell needs ── */
BasicInfoStep.runValidations = (value, { showLocationFields = true } = {}) => {
    const errors = {
        first_name: validateFirstName(value.first_name),
        last_name: validateLastName(value.last_name),
        mobile_number: validateMobile(value.mobile_number),
        email: validateEmail(value.email),
        pincode: validatePincode(value.pincode),
        city: '',
        state: '',
    };
    if (value.pincode && /^\d{6}$/.test(value.pincode) && showLocationFields) {
        if (!value.city?.trim()) errors.city = 'City is required when pincode is entered.';
        if (!value.state?.trim()) errors.state = 'State is required when pincode is entered.';
    }
    return errors;
};

BasicInfoStep.hasErrors = (errors) => Object.values(errors).some((e) => e !== '');

export default BasicInfoStep;
